import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { RootState } from 'features/app/store/store';
import type { WsBridgeConnectionStatus } from 'features/wsbridge/interfaces';

export interface WsBridgeState {
  connectionStatus: WsBridgeConnectionStatus;
  callId: number | null;
  lastError: string | null;
  captionEnabled: boolean;
  captionLanguageRequested: string | null;
}

export const initialWsBridgeState: WsBridgeState = {
  connectionStatus: 'disconnected',
  callId: null,
  lastError: null,
  captionEnabled: true,
  captionLanguageRequested: null,
};

const wsBridgeSlice = createSlice({
  name: 'wsBridge',
  initialState: initialWsBridgeState,
  reducers: {
    wsBridgeConnecting: (state, action: PayloadAction<number>) => {
      state.connectionStatus = 'connecting';
      state.callId = action.payload;
      state.lastError = null;
    },
    wsBridgeConnected: (state) => {
      state.connectionStatus = 'connected';
      state.lastError = null;
    },
    wsBridgeDisconnected: (state) => {
      state.connectionStatus = 'disconnected';
    },
    wsBridgeReconnecting: (state) => {
      state.connectionStatus = 'reconnecting';
    },
    wsBridgeFailed: (state, action: PayloadAction<string>) => {
      state.connectionStatus = 'failed';
      state.lastError = action.payload;
    },
    wsBridgeReset: () => initialWsBridgeState,
    captionEnableRequested: (state) => {
      state.captionEnabled = true;
    },
    captionDisableRequested: (state) => {
      state.captionEnabled = false;
    },
    captionLanguageRequested: (state, action: PayloadAction<string>) => {
      state.captionLanguageRequested = action.payload;
    },
  },
});

export const {
  wsBridgeConnecting,
  wsBridgeConnected,
  wsBridgeDisconnected,
  wsBridgeReconnecting,
  wsBridgeFailed,
  wsBridgeReset,
  captionEnableRequested,
  captionDisableRequested,
  captionLanguageRequested,
} = wsBridgeSlice.actions;

export const selectWsBridgeConnectionStatus = ({ wsBridge }: RootState) =>
  wsBridge.connectionStatus;

export const selectIsWsBridgeConnected = ({ wsBridge }: RootState) =>
  wsBridge.connectionStatus === 'connected';

export const selectWsBridgeCallId = ({ wsBridge }: RootState) =>
  wsBridge.callId;

export const selectWsBridgeCaptionEnabled = ({ wsBridge }: RootState) =>
  wsBridge.captionEnabled;

export const selectWsBridgeCaptionLanguageRequested = ({
  wsBridge,
}: RootState) => wsBridge.captionLanguageRequested;

export const wsBridgeReducer = wsBridgeSlice.reducer;
